import Joi, { ValidationResult } from 'joi';

import { AppleLanguageType, LanguageType } from '@lifeserver/shared';

import { EnvironmentNameType } from '../types/enums/envnametype.enum';
import { MediaType } from '../types/enums/mediatype.enum';
import { PackType } from '../types/enums/packtype.enum';

import { Media } from '../types/mediaTypes';
import { Pack, PackListItem } from '../types/packTypes';

import { redirectToLogin } from './redirectToLogin';

import logoImg from '../assets/logo.svg';
import logoImgDev from '../assets/logo-dev.svg';
import logoImgLocal from '../assets/logo-local.svg';
import logoImgProd from '../assets/logo-prod.svg';
import logoImgStaging from '../assets/logo-staging.svg';

/**
 * Handles errors by logging the error message and optionally displaying an alert to the user.
 * If the error status is 403, redirects the user to the login page.
 * If the error status is 401, informs the user about insufficient rights.
 *
 * @param {string} message - The error message to be logged and optionally displayed.
 * @param {any} error - The error instance to be handled. Error needs to be `any` here as we use this function with a variety of props.
 * @param {boolean} [showAlert=true] - A flag indicating whether to show an alert for unrecognised errors to the user. Shows by default.
 * @returns {string} - The processed error message from the error instance. Does not include the message provided in the first argument.
 */
export function handleError(
    message: string,

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any,
    showAlert: boolean = true,
): string {
    const errorMessage: string =
        error?.response?.data?.message ??
        error?.message ??
        JSON.stringify(error);
    const status: number | undefined = error?.response?.status;

    console.error(`${message}:`, errorMessage);
    if (status === 401) {
        alert('You have been logged out, redirecting to login page.');

        redirectToLogin();

        return '';
    } else if (status === 403) {
        const message =
            'The account has insufficient rights to perform this action.';
        alert(message);

        return message;
    } else if (showAlert) {
        alert(`${message}:\n\n${errorMessage}`);
    }
    return errorMessage;
}

export function arrayMove<T>(
    arr: T[],
    oldIndex: number,
    newIndex: number,
): T[] {
    const arrayLength = arr.length;

    // Adjust negative indixes
    const adjustIndex = (index: number) =>
        index < 0 ? index + arrayLength : index;

    oldIndex = adjustIndex(oldIndex);
    newIndex = adjustIndex(newIndex);

    // Adjust array length if new index is greater than or equal to array length
    if (newIndex >= arrayLength) {
        arr.push(...Array(newIndex - arrayLength + 1).fill(undefined));
    }

    // Move element
    const elementToMove = arr.splice(oldIndex, 1)[0];
    arr.splice(newIndex, 0, elementToMove);

    return arr;
}

export function getPackDisplayName(pack: Pack): string {
    let name = 'no name';
    if (pack && pack.name) name = pack.name;
    // Show display name only for playsets, homedesigner, outfit maker, and character creator packs
    if (
        pack &&
        pack.type !== PackType.MULTIPACK &&
        pack.displayName &&
        pack.displayName.en
    )
        name = pack.displayName.en;
    return name;
}

export const languages: Record<LanguageType, string> = {
    en: 'English',
    ar: 'Arabic',
    zh: 'Chinese',
    'zh-Hant': 'Chinese traditional',
    da: 'Danish',
    nl: 'Dutch',
    fi: 'Finnish',
    fr: 'French',
    'fr-CA': 'French (Canada)',
    de: 'German',
    id: 'Indonesian',
    it: 'Italian',
    ja: 'Japanese',
    ko: 'Korean',
    nb: 'Norwegian',
    pl: 'Polish',
    'pt-BR': 'Portuguese (Brazil)',
    pt: 'Portuguese',
    ru: 'Russian',
    'es-MX': 'Spanish (Mexico)',
    es: 'Spanish',
    sv: 'Swedish',
    th: 'Thai',
    tr: 'Turkish',
};

// https://docs.fastlane.tools/actions/deliver#available-language-codes
// map CMS locale names to Apple locale names
export const appleLanguageMapping: Record<AppleLanguageType, string> = {
    en: 'en-US',
    zh: 'zh-Hans',
    'zh-Hant': 'zh-Hant',
    fr: 'fr-FR',
    de: 'de-DE',
    sv: 'sv',
    es: 'es-ES',
    da: 'da',
    nb: 'no',
    ja: 'ja',
    it: 'it',
    'es-MX': 'es-MX',
    ru: 'ru',
    nl: 'nl-NL',
    'fr-CA': 'fr-CA',
    pt: 'pt-PT',
    'pt-BR': 'pt-BR',
    ko: 'ko',
    fi: 'fi',
    id: 'id',
    th: 'th',
    tr: 'tr',
    pl: 'pl',
    ar: 'ar-SA',
    vi: 'vi',
};

// Fixme move it to a function or something
// Fixme Remove partial as we iterate though all languages
export const displayNameLanguagesSchema: Partial<
    Record<LanguageType, Joi.StringSchema>
> = {};
export const descriptionLanguagesSchema: Partial<
    Record<LanguageType, Joi.StringSchema>
> = {};
// Fixme Replace for..in with something safer
for (const l in languages) {
    displayNameLanguagesSchema[l as LanguageType] = Joi.string()
        .required()
        .messages({
            'string.base': `"${l}" for display name must be a type of string`,
            'string.empty': `"${l}" for display name is missing`,
            'any.required': `"${l}" for display name is missing`,
        });

    descriptionLanguagesSchema[l as LanguageType] = Joi.string()
        .strict()
        .trim()
        .required()
        .messages({
            'string.base': `"${l}" description must be a type of string`,
            'string.empty': `"${l}" description is missing`,
            'string.trim': `"${l}" description must not have leading or trailing whitespace`,
            'any.required': `"${l}" description is missing`,
        });
}

export const platforms = {
    google: 'Google',
    apple: 'Apple',
    amazon: 'Amazon',
};

export const blockedVersionTypes = {
    store: 'shop',
    app: 'app',
};

export function isProductInactive(product: {
    activeFromDate: string | Date;
    activeToDate: string | Date;
}): boolean {
    const from = new Date(product.activeFromDate);
    const to = new Date(product.activeToDate);
    const now = new Date();
    return now < from || now > to;
}

export function moveListItem<T>(
    list: Iterable<T>,
    fromIndex: number,
    toIndex: number,
): T[] {
    const result = Array.from(list);
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);

    return result;
}

export function getPackThumbnail(pack: Pack | PackListItem): Media | undefined {
    return pack.medias.find(
        (media: Media) => media.type === MediaType.THUMBNAIL,
    );
}

/**
 * Converts an array of objects into a CSV (Comma-Separated Values) string format.
 *
 * @template T
 * @param {T[]} data - An array of objects where each object has a string key and an associated value of any type.
 * @param {string} [separator=','] - A string used to separate values in the CSV output. Defaults to a comma.
 * @returns {string | null} - Returns a string formatted as CSV if the input data is valid; returns `null` if:
 *   - The input `data` is not an array.
 *   - The `data` array is empty.
 *
 * @example
 * const data = [
 *     { name: "Alice", age: 30, city: "New York" },
 *     { name: "Bob", age: 25, city: "Los Angeles" }
 * ];
 * console.log(toCsv(data));
 * // Output:
 * // name,age,city
 * // Alice,30,New York
 * // Bob,25,Los Angeles
 */
export function toCsv<T extends Record<string, unknown>>(
    data: T[],
    separator: string = ',',
): string | null {
    if (!data || !Array.isArray(data) || !data.length) {
        return null;
    }

    const keys = Array.from(new Set(data.flatMap((item) => Object.keys(item))));

    let csv = `${keys.join(separator)}\n`;

    for (const row of data) {
        // Object.values doesn't guarantee the order, so only requesting values by array of known keys
        const values = keys.map((key: string) => String(row[key] ?? ''));

        csv += `${values.join(separator)}\n`;
    }

    return csv;
}

export function getLogoByEnvName(envName: EnvironmentNameType): string {
    switch (envName) {
        case EnvironmentNameType.Local:
            return logoImgLocal;
        case EnvironmentNameType.Dev:
            return logoImgDev;
        case EnvironmentNameType.Staging:
            return logoImgStaging;
        case EnvironmentNameType.Production:
            return logoImgProd;
        default:
            return logoImg;
    }
}

export type SectionAttention = {
    severity: 'error' | 'warning';
    messages: string[];
};

export function computeSectionAttention(
    validationResults: {
        errors: ValidationResult;
        warnings: ValidationResult;
    },
    fieldPaths: string[],
): SectionAttention | undefined {
    const scoped = (result: ValidationResult): string[] => {
        const details = result.error?.details ?? [];
        return details
            .filter(
                (d) =>
                    d.path.length > 0 && fieldPaths.includes(String(d.path[0])),
            )
            .map((d) => d.message);
    };

    const errorMessages = scoped(validationResults.errors);
    if (errorMessages.length > 0) {
        return { severity: 'error', messages: errorMessages };
    }

    const warningMessages = scoped(validationResults.warnings);
    if (warningMessages.length > 0) {
        return { severity: 'warning', messages: warningMessages };
    }

    return undefined;
}
